import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff, AlertTriangle } from "lucide-react";
import { renderArticleHTML } from "@/lib/article";

/* ==================== Long-form article renderer ==================== */
export const ArticleRenderer = ({ markdown, citations, className = "" }) => {
  const html = renderArticleHTML(markdown, citations);
  if (!html) return null;
  return (
    <div
      className={`article-prose ${className}`}
      data-testid="article-prose"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

/* ==================== Scroll reveal wrapper ==================== */
export const Reveal = ({ children, className = "", delay = 0, as: Tag = "div", ...props }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Fallback: guarantee visibility even if IntersectionObserver misfires.
    const fallback = setTimeout(() => setVisible(true), 1200);
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return () => clearTimeout(fallback);
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
          clearTimeout(fallback);
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "reveal-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...props}
    >
      {children}
    </Tag>
  );
};

/* ==================== Small uppercase tracked label ==================== */
export const SectionLabel = ({ children, className = "", ...props }) => (
  <span className={`showcase-label ${className}`} {...props}>
    {children}
  </span>
);

/* ==================== Provenance status badge ==================== */
const STATUS_STYLE = {
  Documented: "border-[#1A1918] text-[#1A1918]",
  Verified: "border-[#3F6B4C] text-[#3F6B4C]",
  "Under Review": "border-[#B8860B] text-[#B8860B]",
  Unverified: "border-[#9A9790] text-[#5C5A56]",
};

export const ProvenanceStatusBadge = ({ status, className = "" }) => {
  const style = STATUS_STYLE[status] || STATUS_STYLE.Documented;
  return (
    <span
      className={`inline-flex items-center border rounded-none px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-semibold bg-transparent ${style} ${className}`}
      data-testid="provenance-status-badge"
    >
      {status || "Documented"}
    </span>
  );
};

/* ==================== Image with graceful fallback ==================== */
export const FramedImage = ({ src, alt, className = "", imgClassName = "", pending = false }) => {
  const [errored, setErrored] = useState(false);
  return (
    <div className={`showcase-frame overflow-hidden relative ${className}`}>
      {src && !errored ? (
        <img
          src={src}
          alt={alt || ""}
          loading="lazy"
          onError={() => setErrored(true)}
          className={`showcase-card-img w-full h-full object-cover ${imgClassName}`}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-[#EAE8E0] text-[#9A9790]">
          <ImageOff className="w-8 h-8" />
        </div>
      )}
      {pending && (
        <span
          className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#8C271E] text-white text-[10px] tracking-[0.12em] uppercase font-semibold px-2 py-1"
          data-testid="image-pending-badge"
        >
          <AlertTriangle className="w-3 h-3" /> Image pending replacement
        </span>
      )}
    </div>
  );
};

/* ==================== Gallery item card ==================== */
const PSA_ACTIVE_STATES = ["Submitted to PSA", "Graded", "Returned from PSA"];

export const ItemCard = ({ item }) => {
  const primaryImg = item.images?.[0];
  const psaActive = PSA_ACTIVE_STATES.includes(item.psa_status);
  return (
    <Link
      to={`/collection/item/${item.slug}`}
      className="showcase-card group block"
      data-testid={`collection-item-card-${item.slug}`}
    >
      <FramedImage src={primaryImg?.url} alt={item.title} className="aspect-[4/3]" pending={!!primaryImg?.is_placeholder} />
      <div className="pt-4">
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <SectionLabel className="text-[#8C271E]">{item.category}</SectionLabel>
          <ProvenanceStatusBadge status={item.provenance_status} />
        </div>
        <h3 className="showcase-serif text-xl text-[#1A1918] leading-snug group-hover:text-[#8C271E] transition-colors">
          {item.title}
        </h3>
        {item.short_description && (
          <p className="mt-2 text-sm text-[#5C5A56] leading-relaxed line-clamp-2">
            {item.short_description}
          </p>
        )}
        {(item.graceland_displayed || psaActive) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {item.graceland_displayed && (
              <span className="inline-flex items-center border border-[#D6D3C8] rounded-none px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase text-[#5C5A56]">
                Graceland
              </span>
            )}
            {psaActive && (
              <span className="inline-flex items-center border border-[#3F6B4C] rounded-none px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase text-[#3F6B4C]">
                {item.psa_grade || item.psa_status}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

/* ==================== Featured category card ==================== */
export const CategoryCard = ({ category, image, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className="showcase-card group text-left"
    data-testid={`collection-category-${category.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
  >
    <FramedImage src={image} alt={category.name} className="aspect-[3/2]" />
    <div className="pt-4 flex items-baseline justify-between gap-3">
      <h3 className="showcase-serif text-lg text-[#1A1918] group-hover:text-[#8C271E] transition-colors">
        {category.name}
      </h3>
      <span className="text-sm tabular-nums text-[#5C5A56]">
        {category.count} {category.count === 1 ? "item" : "items"}
      </span>
    </div>
  </button>
);

/* ==================== Provenance timeline ==================== */
export const ProvenanceTimeline = ({ events }) => (
  <ol className="relative border-l border-[#8C271E]/30 ml-2" data-testid="provenance-timeline">
    {events.map((ev, idx) => (
      <Reveal
        as="li"
        key={ev.id}
        delay={Math.min(idx * 60, 300)}
        className="relative pl-8 md:pl-10 pb-14 last:pb-0"
      >
        <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[#8C271E] ring-4 ring-[#F5F4F0]" />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2">
          <SectionLabel className="text-[#8C271E]">{ev.date_label}</SectionLabel>
          {ev.phase && (
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A56]">
              {ev.phase}
            </span>
          )}
          <ProvenanceStatusBadge status={ev.verification_status} />
        </div>
        <h3 className="showcase-serif text-2xl text-[#1A1918] leading-snug mb-2">
          {ev.title}
        </h3>
        <p className="text-[15px] text-[#5C5A56] leading-relaxed max-w-2xl">
          {ev.narrative}
        </p>
        {ev.images?.length > 0 && (
          <div className="mt-4 flex gap-3">
            {ev.images.slice(0, 3).map((im, i) => (
              <FramedImage
                key={i}
                src={im.url}
                alt={im.caption || ev.title}
                className="w-24 h-16 md:w-28 md:h-20"
              />
            ))}
          </div>
        )}
      </Reveal>
    ))}
  </ol>
);
